import { AddCircleOutline, ArrowBack, SearchOutlined, Sort } from '@material-ui/icons'
import { Divider, Drawer, FormControl, FormControlLabel, FormGroup, IconButton, InputAdornment, List, ListItem, MenuItem, Popper, Select, SelectChangeEvent, Skeleton, Stack, Switch, TextField } from '@mui/material'
import React from 'react'
import Matchups from './Matchups'
import { useDispatch, useSelector } from 'react-redux'
import { Projection } from '../common/EasyProjection/types'
import { addPlayer, removePlayer } from './store/actions'
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import ConfirmedLineupIcons from '../common/Table/ConfirmedLineupIcons'
import { IRootState } from '../../store/reducer'
import { Lineup } from './store/types'
import positionToNameDict from '../common/helpers/PositionAbbrevToNameDict'
import { canAddPlayerToSlot, prepareLineupProjections } from './helpers/specialPositionsHelper'
import { formatGameTimeET } from './helpers/formatGameTimeET'
import { multipiers } from '../common/Table/LineupBuilderConfig'
import { ABOVE_BOTTOM_NAV_ZINDEX } from '../common/MobileAppBottomNav'


// Header + matchups + search/sort + filter rows above the virtualized list.
const LIST_CHROME_HEIGHT = 230;
const MIN_LIST_HEIGHT = 200;

const PlayerModal = ({ isOpen, position, onExit }: { isOpen: boolean, position: string, onExit: () => void }) => {
  const [projections, setProjections] = React.useState<Projection[]>([]);
  const [search, setSearch] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [sortBy, setSortBy] = React.useState('proj');
  const [toggledSalary, setToggledSalary] = React.useState(false);
  const [toggledProj, setToggledProj] = React.useState(true);
  const { selectedSlate } = useSelector((state: any) => state.common);
  const { selectedMatchups, lineups, selectedLineupId } = useSelector((state: IRootState) => state.lineupBuilder);
  const selectedLineup = lineups.find((lineup) => lineup.id === selectedLineupId) || {} as Lineup;
  const salaryRemaining = selectedSlate?.lineupRules?.teamSalary - selectedLineup?.players?.reduce((partialSum, a) => partialSum + a.salary, 0);

  const getListHeight = () => Math.max(MIN_LIST_HEIGHT, window.innerHeight - LIST_CHROME_HEIGHT);
  const [listHeight, setListHeight] = React.useState(getListHeight);
  React.useEffect(() => {
    const onResize = () => setListHeight(getListHeight());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleChange = (event: SelectChangeEvent) => {
    setSortBy(event.target.value as string);
    if (event.target.value === 'proj') {
      setProjections([...projections].sort((a: Projection, b: Projection) => b.projection - a.projection))
    } else if (event.target.value === 'salary') {
      setProjections([...projections].sort((a: Projection, b: Projection) => b.salary - a.salary))
    } else if (event.target.value === 'own') {
      setProjections([...projections].sort((a: Projection, b: Projection) => b.ownership - a.ownership))
    }
  };

  const onToggleProjections = (checked: boolean) => {
    setToggledProj(checked);
  }

  const onToggleSalary = (checked: boolean) => {
    setToggledSalary(checked);
  }

  const filterProjections = (projections: Projection[]) => {
    let proj = projections;
    if (toggledProj) {
      proj = proj.filter((x: Projection) => x.projection > 0)
    }
    if (toggledSalary) {
      proj = proj.filter((x: Projection) => x.salary <= salaryRemaining)
    }

    return proj;
  }

  const getPosition = (position: string) => {
    if (positionToNameDict[position] === undefined) return position;
    return positionToNameDict[position];
  }

  const Row = ({ data, index, style }: ListChildComponentProps) => {
    const player = data[index];
    return (
      <ListItem key={index} sx={{ border: '1px solid rgba(255, 255, 255, 0.05)', padding: '10px 16px' }} style={style}>
        <PlayerCard key={index} player={player} onAdd={(position: string) => { onExit() }} position={position} />
      </ListItem>
    )
  }

  React.useEffect(() => {
    const getProjection = async () => {
      if (selectedSlate.slateId === undefined) return;
      let currentProjections = await fetch(`${process.env.REACT_APP_API_URL}/api/slatedata/projections?SlateId=${selectedSlate.slateId}`).then(res => res.json())
      const sessionProjections = sessionStorage.getItem(`lineupBuilderProjections-${selectedSlate.slateId}`);
      currentProjections = prepareLineupProjections(
        currentProjections ?? [], selectedSlate, sessionProjections ? JSON.parse(sessionProjections) : [],
      );
      let filteredPlayers = currentProjections.filter((player: any) => {
        if (selectedMatchups.length === 0) return true;
        return selectedMatchups.includes(player.team) || selectedMatchups.includes(player.opponent)
      })

      filteredPlayers = filterProjections(filteredPlayers);

      if (sortBy === 'proj') {
        setProjections(filteredPlayers.sort((a: Projection, b: Projection) => b.projection - a.projection))
      } else if (sortBy === 'salary') {
        setProjections(filteredPlayers.sort((a: Projection, b: Projection) => b.salary - a.salary))
      } else if (sortBy === 'own') {
        setProjections(filteredPlayers.sort((a: Projection, b: Projection) => b.ownership - a.ownership))
      } else {
        setProjections(filteredPlayers)
      }
      setLoading(false);
      return filteredPlayers;
    }

    if (isOpen) {
      getProjection();
    }

  }, [selectedSlate, selectedMatchups, toggledProj, toggledSalary, isOpen])

  const positionName = selectedSlate?.sport === 'NBA' && getPosition(position) === 'Catcher' ? 'Center' : getPosition(position);

  return (
    <Drawer open={isOpen} onClose={onExit} PaperProps={{ sx: { width: '100%' } }} sx={{ overflow: 'hidden', zIndex: ABOVE_BOTTOM_NAV_ZINDEX }}>
      <Stack direction='column' height='100%' sx={{ fontFamily: 'Inter' }}>
        <Stack direction='row' alignItems='center' bgcolor="#1C1C1C" padding="8px 10px" color='white' fontSize='14px' fontWeight={500}>
          <IconButton aria-label="Back" onClick={onExit} sx={{ color: 'white', padding: '4px', marginRight: '4px' }}>
            <ArrowBack />
          </IconButton>
          Add a {positionName} ({position})
        </Stack>
        <Matchups />
        <Stack direction='column' bgcolor='#0A0A0A' flexGrow={1}>
          {/* Search + Sort on one row */}
          <Stack direction='row' padding='8px 10px' gap={1} alignItems='center'>
            <TextField
              placeholder="Search Players"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              inputProps={{ 'aria-label': 'Search players' }}
              InputProps={{
                startAdornment: <InputAdornment position='start' ><SearchOutlined style={{ color: 'rgba(255, 255, 255, .7)', fontSize: 18 }} /></InputAdornment>,
                sx: { color: 'white', padding: '2px 8px', fontSize: '13px' }
              }}
              sx={{ border: '1px solid rgba(255, 255, 255, 0.15)', borderRadius: '6px', flex: 1 }}
              size="small"
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select
                startAdornment={<Sort style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 18, marginRight: 4 }} />}
                value={sortBy}
                onChange={handleChange}
                MenuProps={{ sx: { zIndex: ABOVE_BOTTOM_NAV_ZINDEX + 1 } }}
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  fontSize: '13px',
                  height: '40px',
                  '& svg': { color: 'rgba(255, 255, 255, 0.15)' },
                  '& .MuiSelect-select': { padding: '8px 8px' },
                }}
              >
                <MenuItem value='proj'>Projection</MenuItem>
                <MenuItem value='own'>Ownership</MenuItem>
                <MenuItem value='salary'>Salary</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          {/* Toggle filters */}
          <Stack>
            <FormGroup sx={{ padding: '0 16px 4px', display: 'flex', flexDirection: 'row', color: 'rgba(255, 255, 255, 0.5)', gap: '16px', fontSize: '11px' }}>
              <FormControlLabel onChange={(a, checked: boolean) => onToggleProjections(checked)} control={<Switch size='small' checked={toggledProj} />} label="Hide 0 Proj." sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: '11px' } }} />
              <FormControlLabel onChange={(a, checked: boolean) => onToggleSalary(checked)} control={<Switch size='small' checked={toggledSalary} />} label="Show Affordable" sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: '11px' } }} />
            </FormGroup>
          </Stack>
          <Stack>
            {
              loading && (
                <List>
                  <ListItem sx={{ height: '82px' }}><Skeleton width='100%' height='166%' sx={{ bgcolor: 'rgba(255, 255, 255, .2)' }} /></ListItem>
                  <ListItem sx={{ height: '82px' }}><Skeleton width='100%' height='166%' sx={{ bgcolor: 'rgba(255, 255, 255, .2)' }} /></ListItem>
                  <ListItem sx={{ height: '82px' }}><Skeleton width='100%' height='166%' sx={{ bgcolor: 'rgba(255, 255, 255, .2)' }} /></ListItem>
                  <ListItem sx={{ height: '82px' }}><Skeleton width='100%' height='166%' sx={{ bgcolor: 'rgba(255, 255, 255, .2)' }} /></ListItem>
                  <ListItem sx={{ height: '82px' }}><Skeleton width='100%' height='166%' sx={{ bgcolor: 'rgba(255, 255, 255, .2)' }} /></ListItem>
                  <ListItem sx={{ height: '82px' }}><Skeleton width='100%' height='166%' sx={{ bgcolor: 'rgba(255, 255, 255, .2)' }} /></ListItem>
                </List>
              )
            }
            {
              projections.length > 0 && !loading && (
                <>
                  <Stack bgcolor="#1C1C1C" color='white' fontSize='10px' fontWeight={500} direction='row' justifyContent='space-between' padding='8px 48px 8px 32px'>
                    <Stack>Player</Stack>
                    <Stack direction='row'>
                      <Divider orientation='vertical' flexItem sx={{ borderColor: 'rgba(255, 255, 255, 0.30)', marginRight: '8px' }} />
                      Salary
                    </Stack>
                  </Stack>
                  <FixedSizeList
                    height={listHeight}
                    // width={360}
                    itemData={projections
                      .filter((x: Projection) => {
                        return (
                          canAddPlayerToSlot(x, position)
                          && x.name.toLowerCase().includes(search.toLowerCase()))
                      }
                      )}
                    itemSize={68}
                    itemCount={projections
                      .filter((x: Projection) => {
                        return (
                          canAddPlayerToSlot(x, position)
                          && x.name.toLowerCase().includes(search.toLowerCase()))
                      }).length}
                    overscanCount={5}
                  >
                    {Row}
                  </FixedSizeList>
                </>
              )
            }
          </Stack>
        </Stack>
      </Stack>
    </Drawer>
  )
}

const PlayerCard = ({ player, onAdd, position }: { player: Projection, onAdd: (position: string) => void, position: string }) => {
  const [inLineup, setOnLineup] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const { lineups, selectedLineupId } = useSelector((state: IRootState) => state.lineupBuilder);
  const { selectedSite, selectedSlate } = useSelector((state: IRootState) => state.common);
  const dispatch = useDispatch();


  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',

    // These options are needed to round to whole numbers if that's what you want.
    minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
    maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
  });

  const handleClick = (event: React.MouseEvent<HTMLElement>, singlePosition?: string) => {
    const multiPosition = player.position.includes('/');
    const selectedFanDuelSlot = selectedSlate.site === 'FD' && selectedSlate.sport === 'NBA'
      && selectedSlate.type === 'CLASSIC' && position;
    let bothPositionsAvailable = false;
    if (multiPosition) {
      bothPositionsAvailable = player.position.split('/').map(pos => {
        const lineup = lineups.find(x => x.id === selectedLineupId) || {} as Lineup;
        return lineup.players.find(y => y.position === pos && y.name === '') ? pos : null;
      }).filter(x => x !== null).length === player.position.split('/').length;
    }
    if (inLineup) {
      dispatch(removePlayer(player, selectedLineupId))
      onAdd('');
    } else if (multiPosition && bothPositionsAvailable && !singlePosition && !selectedFanDuelSlot) {
      setAnchorEl(anchorEl ? null : event.currentTarget);
    } else if (singlePosition) {
      dispatch(addPlayer({
        ...player,
        position: singlePosition || position,
      }, selectedLineupId, selectedSite.site))
      setAnchorEl(null);
      onAdd('');
    } else {
      dispatch(addPlayer({
        ...player,
        position: position || player.position,
      }, selectedLineupId, selectedSite.site))
      onAdd('')
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popper' : undefined;

  React.useEffect(() => {
    if (lineups) {
      const lineup = lineups.find(x => x.id === selectedLineupId);
      let found, hasPositionAvailable = false;
      if (lineup) {
        found = lineup.players.find(y => y.name === player.name);
        hasPositionAvailable = lineup.players.some(y => canAddPlayerToSlot(player, y.position) && y.name === '');
      }

      if (found) {
        setOnLineup(true);
      } else if (!hasPositionAvailable) {
      } else {
        setOnLineup(false);
      }
    }
  }, [lineups, selectedLineupId]);

  return (
    <Stack direction='row' justifyContent='space-between' columnGap='10px' width='100%'>
      <Stack direction='row' justifyContent='center' alignItems='center' color='rgba(255, 255, 255, 0.87)' fontSize='12px' fontWeight={500} sx={{ opacity: inLineup ? .5 : 1 }}>
        {player.position}
      </Stack>
      <Stack direction='row' flexGrow={1} flexShrink={1} justifyContent='space-between' alignItems='center' sx={{ opacity: inLineup ? .5 : 1 }}>
        <Stack direction='column' rowGap='4px'>
          <Stack direction='row' alignItems='center'>
            {/* <Stack sx={{
              '& img': {
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                objectFit: 'contain',
                marginRight: '4px'
              }
            }}>
              <img src='https://awesemostorage.blob.core.windows.net/player-headshots-v2/NBA/120x120-crop/20001796' alt='' />
            </Stack> */}
            <Stack fontSize='12px' fontWeight={500} color='white'>{player.name || 'Derrick Jones Jr.'}</Stack>
            <Stack paddingLeft='4px'>
              {
                selectedSlate.sport?.toLowerCase() === 'mlb' && (
                  <ConfirmedLineupIcons confirmed={player.confirmedLineup} battingOrder={player.lineupPosition} />
                )
              }
            </Stack>
          </Stack>
          <Stack direction='row' alignItems='center' columnGap='4px'>
            <Stack direction='row' alignItems='center' sx={{
              '& img': {
                width: '10px',
                height: '10px',
                objectFit: 'contain',
                marginRight: '4px'
              }
            }}>
              <Stack color="rgba(255, 255, 255, 0.70)" fontSize='10px' fontWeight={900}>{player.team}</Stack>
              {
                ['mlb', 'nba', 'nhl', 'nfl'].indexOf(selectedSlate?.sport?.toLowerCase()) > -1 && (
                  <img src={player.teamLogo} alt='' />
                )}
            </Stack>
            <Stack color="rgba(255, 255, 255, 0.70)" fontSize='10px' fontWeight={400}>vs</Stack>
            <Stack direction='row' alignItems='center' sx={{
              '& img': {
                width: '10px',
                height: '10px',
                objectFit: 'contain',
                marginRight: '4px'
              }
            }}>
              <Stack color="rgba(255, 255, 255, 0.70)" fontSize='10px' fontWeight={900}>{player.opponent}</Stack>
              {
                ['mlb', 'nba', 'nhl', 'nfl'].indexOf(selectedSlate?.sport?.toLowerCase()) > -1 && (
                  <img src={player.opponentLogo} alt='' />
                )}
            </Stack>
          </Stack>
          <Stack color="rgba(255, 255, 255, 0.70)" fontSize='10px' fontWeight={400}>
            {formatGameTimeET(player.gameTime)}
          </Stack>
        </Stack>
        <Stack direction='row'>
          <Stack direction='column' alignItems='flex-end'>
            <Stack color="rgba(255, 255, 255, .7)" fontSize='12px' fontWeight={400} whiteSpace='nowrap'>FPP {multipiers[position] !== undefined ? multipiers[position] * player.projection : (player.projection.toFixed(2) || '-')}</Stack>
            <Stack color="rgba(255, 255, 255, .7)" fontSize='12px' fontWeight={400} whiteSpace='nowrap'>OWN {(player.ownership * 100).toFixed(2) + '%' || '-'}</Stack>
          </Stack>
          <Divider orientation='vertical' flexItem sx={{ mx: '8px', borderColor: 'rgba(255, 255, 255, 0.30)' }} />
        </Stack>
      </Stack>
      <Stack>
        <Stack direction='row' columnGap='8px' alignItems='center' justifyContent='space-between' height='100%' width='100%'>
          <Stack color='white' fontSize='14px' fontWeight={400} sx={{ opacity: inLineup ? .5 : 1 }}>{formatter.format(['CPT', 'MVP - 1.5x Points', 'MVP - 1.5X Points'].includes(position) ? player.salary * 1.5 : player.salary)}</Stack>
          <Stack aria-describedby={id} sx={{ height: '24px', width: '24px', cursor: 'pointer' }} onClick={(e) => {
            handleClick(e);
          }}>
            {inLineup ? <MinusIcon /> : <AddIcon />}
          </Stack>
          <Popper id={id} open={open} anchorEl={anchorEl} disablePortal placement='left' sx={{
            padding: '8px',
            backgroundColor: 'rgba(35, 35, 35, 1)',
            borderRadius: '6px',
            fontFamily: 'Inter',
            zIndex: 1300,
          }}>
            {
              player.position.split('/').map((pos, index) => (
                <Stack onClick={(e) => handleClick(e, pos)} direction='row' alignItems='center' justifyContent='center' columnGap='4px' key={index} color='white' sx={{ cursor: 'pointer', padding: '4px', borderRadius: '4px', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.05)' } }}>
                  Add to {pos} <AddCircleOutline style={{ color: '#368D39' }} />
                </Stack>
              )
              )
            }
          </Popper>
        </Stack>
      </Stack>
    </Stack>
  )
}

const AddIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <rect x="0.25" y="0.25" width="23.5" height="23.5" rx="11.75" fill="#00B553" fillOpacity="0.15" />
    <rect x="0.25" y="0.25" width="23.5" height="23.5" rx="11.75" stroke="#00B553" stroke-width="0.5" />
    <path d="M11.5 12.5H6.5C6.35833 12.5 6.23958 12.452 6.14375 12.3561C6.04792 12.2601 6 12.1412 6 11.9993C6 11.8575 6.04792 11.7388 6.14375 11.6433C6.23958 11.5478 6.35833 11.5 6.5 11.5H11.5V6.5C11.5 6.35833 11.548 6.23958 11.6439 6.14375C11.7399 6.04792 11.8588 6 12.0007 6C12.1425 6 12.2612 6.04792 12.3567 6.14375C12.4522 6.23958 12.5 6.35833 12.5 6.5V11.5H17.5C17.6417 11.5 17.7604 11.548 17.8562 11.6439C17.9521 11.7399 18 11.8588 18 12.0007C18 12.1425 17.9521 12.2612 17.8562 12.3567C17.7604 12.4522 17.6417 12.5 17.5 12.5H12.5V17.5C12.5 17.6417 12.452 17.7604 12.3561 17.8562C12.2601 17.9521 12.1412 18 11.9993 18C11.8575 18 11.7388 17.9521 11.6433 17.8562C11.5478 17.7604 11.5 17.6417 11.5 17.5V12.5Z" fill="#00B553" />
  </svg>
)

const MinusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <rect width="24" height="24" rx="12" fill="#FF4358" fillOpacity="0.1" />
    <rect x="0.5" y="0.5" width="23" height="23" rx="11.5" stroke="#FF4358" stroke-opacity="0.5" />
    <path d="M5 12.75V11.25H19V12.75H5Z" fill="#FF4358" />
  </svg>
)

export default PlayerModal
